export interface MetricTotals {
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  failed: number;
  opens: number;
  clicks: number;
}

export interface MetricRates {
  /** Percentage of attempts that landed in an inbox. */
  delivery: number;
  bounce: number;
  complaint: number;
  open: number;
  click: number;
}

export interface DailyVolume {
  /** ISO date (midnight local). */
  date: string;
  sent: number;
  delivered: number;
}

export interface CampaignRow {
  id: string;
  subject: string;
  audience: string;
  sentAt: string;
  sent: number;
  delivered: number;
  bounced: number;
  opens: number;
  clicks: number;
}

export interface SenderReputation {
  score: number;
  grade: 'A' | 'B' | 'C' | 'D';
  label: string;
  /** What moved the score, in the sender's own terms. */
  note: string;
}

export interface SenderMetrics {
  sample: true;
  windowDays: number;
  from: string;
  to: string;
  daily: DailyVolume[];
  totals: MetricTotals;
  /** The window immediately before this one, for the deltas on the tiles. */
  previous: MetricTotals;
  previousFrom: string;
  previousTo: string;
  // rates: MetricRates;
  // reputation: SenderReputation;
  // campaigns: CampaignRow[];
}

export interface EnrichedSenderMetrics extends SenderMetrics {
  rates: MetricRates;
  reputation: SenderReputation;
}

export type MetricTone = 'ok' | 'watch' | 'bad';

/**
 * The thresholds providers actually enforce. `good` is the line where nobody
 * complains, `watch` the line where deliverability starts to suffer; for a
 * higher-is-better metric the two are the same numbers read the other way.
 */
export const METRIC_THRESHOLDS = {
  bounce: { good: 2, watch: 4 },
  complaint: { good: 0.1, watch: 0.3 },
  unsubscribe: { good: 0.4, watch: 1 },
  delivery: { good: 97, watch: 94 },
  open: { good: 25, watch: 15 },
  click: { good: 3, watch: 1.5 },
} as const;

/** Tint a rate tile: lower-is-better unless `higherIsBetter`. */
export function rateTone(
  value: number,
  good: number,
  watch: number,
  higherIsBetter = false,
): MetricTone {
  if (higherIsBetter) {
    if (value >= good) return 'ok';
    return value >= watch ? 'watch' : 'bad';
  }
  if (value <= good) return 'ok';
  return value <= watch ? 'watch' : 'bad';
}

export const TONE_TEXT: Record<MetricTone, string> = {
  ok: 'text-ok',
  watch: 'text-warn',
  bad: 'text-danger',
};

/** `12480` → `12.5k` — for tiles and chart labels. */
export function formatCount(value: number): string {
  if (value >= 10_000) return `${Math.round(value / 1000)}k`;
  if (value >= 1_000) return `${(value / 1000).toFixed(1)}k`;
  return value.toLocaleString();
}

export function formatRate(value: number, digits = 2): string {
  return `${value.toFixed(digits)}%`;
}

/** Percentage of `whole`, safe at zero. */
export function rateOf(part: number, whole: number): number {
  return whole <= 0 ? 0 : (part / whole) * 100;
}

/** Change against the previous window, or `null` when there is no baseline. */
export function deltaPercent(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return ((current - previous) / previous) * 100;
}

function ratesFrom(totals: MetricTotals): MetricRates {
  return {
    delivery: rateOf(totals.delivered, totals.sent),
    bounce: rateOf(totals.bounced, totals.sent),
    complaint: rateOf(totals.complained, totals.delivered),
    open: rateOf(totals.opens, totals.delivered),
    click: rateOf(totals.clicks, totals.delivered),
  };
}

function reputationFrom(rates: MetricRates): SenderReputation {
  const reasons: string[] = [];
  let score = 95;

  if (rates.bounce > METRIC_THRESHOLDS.bounce.watch) {
    score -= 14;
    reasons.push(
      `${formatRate(rates.bounce)} bounce rate is above the 4% ceiling providers enforce`,
    );
  } else if (rates.bounce > METRIC_THRESHOLDS.bounce.good) {
    score -= 6;
    reasons.push(`${formatRate(rates.bounce)} bounce rate is over the 2% most providers prefer`);
  }

  if (rates.complaint > METRIC_THRESHOLDS.complaint.watch) {
    score -= 26;
    reasons.push(
      `${formatRate(rates.complaint)} complaint rate is past the 0.3% line where providers start filtering`,
    );
  } else if (rates.complaint > METRIC_THRESHOLDS.complaint.good) {
    score -= 11;
    reasons.push(`${formatRate(rates.complaint)} complaint rate is above the 0.1% target`);
  }

  const grade: SenderReputation['grade'] =
    score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 65 ? 'C' : 'D';
  const label =
    grade === 'A' ? 'Healthy' : grade === 'B' ? 'Fine' : grade === 'C' ? 'Watch' : 'At risk';

  return {
    score,
    grade,
    label,
    note:
      reasons.length > 0
        ? reasons.join(' · ')
        : 'No rate is over its threshold, bounces and complaints are both in the range providers like.',
  };
}

export function buildSenderMetrics(metrics: SenderMetrics | null): EnrichedSenderMetrics | null {
  if (!metrics) return null;

  const rates = ratesFrom(metrics.totals);

  return {
    ...metrics,
    rates,
    reputation: reputationFrom(rates),
  };
}
